import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  FormHelperText,
} from '@mui/material';
import { Employee } from '../../types/employee.types';
import { useShops } from '../../hooks/useShops';

const employeeSchema = z.object({
  name: z.string().min(2, 'Employee name must be at least 2 characters'),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  phone: z.string().optional(),
});

type EmployeeFormValues = z.infer<typeof employeeSchema>;

interface EmployeeFormProps {
  open: boolean;
  employee?: Employee | null;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    email?: string;
    phone?: string;
    assignedShopIds: string[];
  }) => Promise<void>;
  isLoading?: boolean;
}

export default function EmployeeForm({ open, employee, onClose, onSubmit, isLoading }: EmployeeFormProps) {
  const { shops } = useShops();
  const [selectedShops, setSelectedShops] = useState<string[]>([]);
  const [shopError, setShopError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
  });

  // Reset form when employee changes or dialog opens
  useEffect(() => {
    if (open) {
      reset({
        name: employee?.name || '',
        email: employee?.email || '',
        phone: employee?.phone || '',
      });

      // Set selected shops - all shops if new employee, existing shops if editing
      if (employee?.assignedShopIds) {
        setSelectedShops(employee.assignedShopIds);
      } else {
        // For new employees, select all shops by default
        setSelectedShops(shops.map(shop => shop.id));
      }
      setShopError('');
    }
  }, [open, employee, reset, shops]);

  const handleShopToggle = (shopId: string) => {
    setSelectedShops(prev => {
      if (prev.includes(shopId)) {
        // Don't allow deselecting if it's the last shop
        if (prev.length === 1) {
          setShopError('Employee must be assigned to at least one shop');
          return prev;
        }
        setShopError('');
        return prev.filter(id => id !== shopId);
      } else {
        setShopError('');
        return [...prev, shopId];
      }
    });
  };

  const handleFormSubmit = async (data: EmployeeFormValues) => {
    // Validate at least one shop is selected
    if (selectedShops.length === 0) {
      setShopError('Employee must be assigned to at least one shop');
      return;
    }

    try {
      await onSubmit({
        name: data.name,
        email: data.email,
        phone: data.phone,
        assignedShopIds: selectedShops,
      });
      handleClose();
    } catch (error) {
      console.error('Form submit error:', error);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedShops([]);
    setShopError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>{employee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  {...register('name')}
                  label="Full Name"
                  fullWidth
                  autoFocus
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('email')}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('phone')}
                  label="Phone Number"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                  Shop Assignments *
                </Typography>
                <FormGroup>
                  {shops.map((shop) => (
                    <FormControlLabel
                      key={shop.id}
                      control={
                        <Checkbox
                          checked={selectedShops.includes(shop.id)}
                          onChange={() => handleShopToggle(shop.id)}
                          disabled={isLoading}
                        />
                      }
                      label={shop.name}
                    />
                  ))}
                </FormGroup>
                <FormHelperText error={!!shopError}>
                  {shopError || `Employee can work at ${selectedShops.length} shop${selectedShops.length !== 1 ? 's' : ''}`}
                </FormHelperText>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {employee ? 'Update Employee' : 'Add Employee'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
